import { useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { ToastContainer } from '../Shared/ToastContainer';
import { WhatsNewModal } from '../Shared/WhatsNewModal';
import { MobileTabbar } from '../Dashboard/Employee/MobileTabbar';
import { useAuthStore } from '../../store/useAuthStore';
import { useOpenItemsStore } from '../../store/useOpenItemsStore';

/** Routes whose page fills exactly the viewport and scrolls its panes itself. */
const FULL_HEIGHT_ROUTES = ['/planning', '/planning/assistant'];

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const refreshMyCount = useOpenItemsStore((s) => s.refreshMyCount);

  // Sidebar badge for "Offene Punkte" — system items (e.g. overdue invoices) appear without any
  // action in this tab, so the count is refreshed whenever the window regains focus.
  useEffect(() => {
    if (isSuperAdmin) return;
    refreshMyCount();
    window.addEventListener('focus', refreshMyCount);
    return () => window.removeEventListener('focus', refreshMyCount);
  }, [isSuperAdmin, refreshMyCount]);

  // why: these pages split the viewport into independently scrolling panes (e.g. the week
  // grid next to the due-task list). With min-h-screen the window would scroll instead and
  // carry the grid out of view while dragging.
  const fullHeight = FULL_HEIGHT_ROUTES.includes(pathname);

  return (
    <div className={`bg-paper flex ${fullHeight ? 'h-dvh overflow-hidden' : 'min-h-screen'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className={`flex-1 flex flex-col min-w-0 ${fullHeight ? 'h-full' : 'min-h-screen'}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 flex flex-col min-h-0 overflow-auto">
          {/* pb-20 keeps the last rows clear of the fixed mobile tab bar. */}
          <div className="px-6 py-6 pb-20 md:pb-6 flex flex-col min-h-0 flex-1">
            {children}
          </div>
        </main>
      </div>

      {/* why: rendered once here rather than per page — otherwise the bar disappears on
          every screen that forgets to include it. Hidden from md upwards. */}
      <MobileTabbar />

      <ToastContainer />
      <WhatsNewModal />
    </div>
  );
}
